import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Trash2, ExternalLink, ChevronDown } from "lucide-react";

const LABELS = { saved: "Saved", applied: "Applied", interviewing: "Interviewing", offer: "Offer", rejected: "Rejected", closed: "Closed" };
const TONE = {
  saved: "border-slate-300 text-slate-700",
  applied: "border-sky-200 text-sky-700",
  interviewing: "border-amber-200 text-amber-700",
  offer: "border-emerald-200 text-emerald-700",
  rejected: "border-rose-200 text-rose-600",
  closed: "border-slate-300 text-slate-500",
};

export const ApplicationTracker = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/applications").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (item, status) => {
    setData(d => ({ ...d, applications: d.applications.map(a => a.id === item.id ? { ...a, status } : a) }));
    try { await api.patch(`/applications/${item.id}`, { status }); await load(); }
    catch { toast.error("Could not update that application"); load(); }
  };

  const remove = async (item) => {
    try { await api.delete(`/applications/${item.id}`); await load(); toast.success("Removed from your tracker"); }
    catch { toast.error("Could not remove that application"); }
  };

  if (!data) return null;
  const items = data.applications;
  const shown = open ? items : items.slice(0, 4);

  return (
    <section className="h-full rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5 flex flex-col" data-testid="application-tracker">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Briefcase className="w-4 h-4 text-sky-600" /> Application Tracker</h3>
          <p className="text-xs text-slate-500 mt-1">Every role you open with Click To Apply lands here. Update the status as you progress.</p>
        </div>
        <span className="shrink-0 text-xs text-slate-500" data-testid="tracker-total">{data.total} tracked</span>
      </div>

      {!items.length ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="tracker-empty">Nothing tracked yet. Tailor a resume to a vacancy, then use Click To Apply — we'll log it for you.</p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="tracker-counts">
            {Object.entries(data.counts).filter(([, n]) => n > 0).map(([status, n]) => (
              <span key={status} className={`rounded-full border px-2.5 py-1 text-[11px] ${TONE[status]}`} data-testid={`tracker-count-${status}`}>{LABELS[status]}: {n}</span>
            ))}
          </div>
          <ul className="mt-3 space-y-2 flex-1">
            {shown.map((item) => (
              <li key={item.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`tracker-item-${item.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-slate-900 break-words">{item.title}</p>
                    <p className="text-xs text-slate-500 break-words">{item.company}{item.location ? ` · ${item.location}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-500 hover:text-sky-700" title="Open posting" data-testid={`tracker-open-${item.id}`}><ExternalLink className="w-3.5 h-3.5" /></a>}
                    <button onClick={() => remove(item)} className="p-1.5 text-slate-500 hover:text-rose-600" title="Remove" data-testid={`tracker-remove-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                <select value={item.status} onChange={(e) => setStatus(item, e.target.value)} className="mt-2 w-full rounded-md bg-slate-50 border border-slate-300 px-2 py-2 text-xs text-slate-800" data-testid={`tracker-status-${item.id}`}>
                  {data.statuses.map(s => <option key={s} value={s}>{LABELS[s]}</option>)}
                </select>
                {item.applied_at && <p className="mt-1.5 text-[10px] text-slate-500">Applied {new Date(item.applied_at).toLocaleDateString()}</p>}
              </li>
            ))}
          </ul>
          {items.length > 4 && (
            <button onClick={() => setOpen(!open)} className="mt-3 inline-flex items-center gap-1 text-xs text-sky-700" data-testid="tracker-toggle">
              {open ? "Show fewer" : `Show all ${items.length}`} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </>
      )}
    </section>
  );
};

export default ApplicationTracker;
